import { UploadResult } from 'Features/Projects/Table/uploadToasts'
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import { requestV3 } from 'Features/Auth/axios-client'
import {
  ProductSetting,
  ProductChangeover,
} from 'Features/Projects/Scenarios/EditSettings/LineSettings/LineSettings.types'

const fetchProductSettings = async (projectId: string, scenarioId: string) => {
  const { data } = await requestV3({
    url: `/product-settings?projectId=${encodeURIComponent(
      projectId
    )}&scenarioId=${encodeURIComponent(scenarioId)}`,
  })
  return data
}

export const useProductSettings = ({
  projectId,
  scenarioId,
}: {
  projectId: string
  scenarioId: string
}): UseQueryResult<ProductSetting[]> => {
  return useQuery({
    queryKey: ['product-settings', projectId, scenarioId],
    queryFn: () => fetchProductSettings(projectId, scenarioId),
    select: (data) => data.data,
    gcTime: Infinity,
  })
}

const fetchBatchSettings = async (projectId: string, scenarioId: string) => {
  const { data } = await requestV3({
    url: `/batch-settings?projectId=${projectId}&scenarioId=${scenarioId}`,
  })
  return data
}

export const useBatchSettings = ({
  projectId,
  scenarioId,
}: {
  projectId: string
  scenarioId: string
}) => {
  return useQuery({
    queryKey: ['batch-settings', projectId, scenarioId],
    queryFn: () => fetchBatchSettings(projectId, scenarioId),
    select: (data) => data.data,
    gcTime: Infinity,
  })
}

const fetchProductChangeovers = async (
  projectId: string,
  scenarioId: string
) => {
  const { data } = await requestV3({
    url: `/product-changeovers?projectId=${projectId}&scenarioId=${scenarioId}`,
  })
  return data
}

export const useProductChangeovers = ({
  projectId,
  scenarioId,
}: {
  projectId: string
  scenarioId: string
}) => {
  return useQuery({
    queryKey: ['product-changeovers', projectId, scenarioId],
    queryFn: () => fetchProductChangeovers(projectId, scenarioId),
    select: (data) => data.data,
    gcTime: Infinity,
  })
}

// The changed rows of a grid, keyed by row id: what a grid's Save sends.
export interface UpdateSettingsInterface<T> {
  changedRows: Record<string, T>
  projectId: string
  scenarioId: string
}

const updateProductChangeovers = async ({
  changedRows,
  projectId,
  scenarioId,
}: UpdateSettingsInterface<ProductChangeover>) => {
  const { data } = await requestV3({
    url: `/product-changeovers?projectId=${encodeURIComponent(
      projectId
    )}&scenarioId=${encodeURIComponent(scenarioId)}`,
    method: 'PUT',
    data: changedRows,
  })
  return data
}

const uploadBatchSettings = async ({
  file,
  projectId,
  scenarioId,
}: {
  file: File
  projectId: string
  scenarioId: string
}) => {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await requestV3({
    url: `/batch-settings?projectId=${encodeURIComponent(projectId)}&scenarioId=${encodeURIComponent(scenarioId)}`,
    method: 'POST',
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export const useUploadBatchSettings = (
  onSuccess: (result?: UploadResult) => void,
  onError: (error?: unknown) => void
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: uploadBatchSettings,
    onSuccess: (data, { projectId, scenarioId }) => {
      queryClient.invalidateQueries({
        queryKey: ['batch-settings', projectId, scenarioId],
      })
      queryClient.invalidateQueries({
        queryKey: ['product-settings', projectId, scenarioId],
      })
      queryClient.invalidateQueries({
        queryKey: ['base-source-site-workcentre', projectId, scenarioId],
      })
      onSuccess(data)
    },
    onError,
  })
}

const uploadProductSettings = async ({
  file,
  projectId,
  scenarioId,
}: {
  file: File
  projectId: string
  scenarioId: string
}) => {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await requestV3({
    url: `/product-settings?projectId=${encodeURIComponent(projectId)}&scenarioId=${encodeURIComponent(scenarioId)}`,
    method: 'POST',
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export const useUploadProductSettings = (
  onSuccess: (result?: UploadResult) => void,
  onError: (error?: unknown) => void
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: uploadProductSettings,
    onSuccess: (data, { projectId, scenarioId }) => {
      queryClient.invalidateQueries({
        queryKey: ['product-settings', projectId, scenarioId],
      })
      queryClient.invalidateQueries({
        queryKey: ['batch-settings', projectId, scenarioId],
      })
      queryClient.invalidateQueries({
        queryKey: ['base-source-site-workcentre', projectId, scenarioId],
      })
      onSuccess(data)
    },
    onError,
  })
}

const uploadProductChangeovers = async ({
  file,
  projectId,
  scenarioId,
}: {
  file: File
  projectId: string
  scenarioId: string
}) => {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await requestV3({
    url: `/product-changeovers?projectId=${encodeURIComponent(projectId)}&scenarioId=${encodeURIComponent(scenarioId)}`,
    method: 'POST',
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export const useUploadProductChangeovers = (
  onSuccess: (result?: UploadResult) => void,
  onError: (error?: unknown) => void
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: uploadProductChangeovers,
    onSuccess: (data, { projectId, scenarioId }) => {
      queryClient.invalidateQueries({
        queryKey: ['product-changeovers', projectId, scenarioId],
      })
      queryClient.invalidateQueries({
        queryKey: ['base-source-site-workcentre', projectId, scenarioId],
      })
      onSuccess(data)
    },
    onError,
  })
}

export const useUpdateProductChangeovers = (
  onSuccess: () => void,
  // The API's reason, e.g. a negative changeover time.
  onError: (message?: string) => void
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateProductChangeovers,
    onSuccess: (_, { projectId, scenarioId }) => {
      queryClient.invalidateQueries({
        queryKey: ['product-changeovers', projectId, scenarioId],
      })
      queryClient.invalidateQueries({
        queryKey: ['base-source-site-workcentre', projectId, scenarioId],
      })
      onSuccess()
    },
    onError: (error: { response?: { data?: { error?: string } } }) =>
      onError(error?.response?.data?.error),
  })
}
